// @ts-nocheck
import React from 'react';
import { Popup } from "react-map-gl";
import * as turf from '@turf/turf';
import useIsMobile from '../../../hooks/useIsMobile';
import { getForecastUnit } from '../utils/forecastUtils';
import AssetImage from '../../AssetImage';
import { ReactComponent as HomeSmile } from '../../../assets/icons/home-smile.svg';

interface FeatureProperty {
  id: string;
  name: string;
  description?: string;
  forecastValue: number | "N/A" | null;
  assetGroupId?: string;
  color?: string;
  imageUrl?: string;
  asset?: any; // Full asset object for AssetImage component
}

interface GeometryPoint {
  type: "Point";
  coordinates: [number, number];
}

interface GeometryPolygon {
  type: "Polygon";
  coordinates: number[][][];
}

interface GeometryLineString {
  type: "LineString" | "MultiLineString";
  coordinates: number[][] | number[][][];
}

type Geometry = GeometryPoint | GeometryPolygon | GeometryLineString;

interface Feature {
  type: "Feature";
  geometry: Geometry;
  properties: FeatureProperty;
}

interface HoveredFeature {
  feature: Feature;
  longitude: number;
  latitude: number;
}

interface MapPopupsProps {
  hoveredFeature: HoveredFeature | null;
  showAllTooltips: boolean;
  forecastType: string;
  assetGeoJSON?: { features: Feature[] };
}

/**
 * Component that renders map popups for assets
 * Handles both hover popups and "show all" popups
 */
const MapPopups: React.FC<MapPopupsProps> = ({ 
  hoveredFeature, 
  showAllTooltips, 
  forecastType, 
  assetGeoJSON 
}) => {
  const isMobile = useIsMobile();
  
  // Helper to check if forecastValue is a valid number
  const isValidForecastValue = (value: any): boolean => {
    return value !== "N/A" && 
           value !== null && 
           value !== undefined && 
           typeof value === 'number' && 
           !isNaN(value);
  };

  // Helper function to render a single asset popup
  const renderAssetPopup = (feature: Feature, longitude: number, latitude: number, key?: string) => {
    // Skip if no forecast value is available
    if (!feature.properties || feature.properties.forecastValue == null) return null;

    if (isMobile) {
      return (
        <Popup
          key={key}
          longitude={longitude}
          latitude={latitude}
          closeButton={!showAllTooltips}
          closeOnClick={!showAllTooltips}
          anchor="bottom"
          offset={[0, -8]} // Increased offset for better visibility
          className={showAllTooltips ? 'asset-popup all-features-popup' : 'asset-popup'}
          maxWidth="240px"
        >
          <div className="text-base">
            <div className="flex items-center gap-3 mb-2">
              <AssetImage 
                asset={feature.properties.asset}
                imageUrl={feature.properties.imageUrl}
                assetName={feature.properties.name}
                size={32}
                fallbackIcon={<HomeSmile className="h-4 w-4" />}
              />
              <div className="font-semibold text-gray-800">
                {feature.properties.name}
              </div>
            </div>
            <div className="mt-2 flex items-center">
              <span className="font-medium text-blue-600">
                {isValidForecastValue(feature?.properties?.forecastValue)
                  ? feature?.properties?.forecastValue?.toFixed(2)
                  : "N/A"}
              </span>
              <span className="ml-1 text-gray-600">
                {getForecastUnit(forecastType)}
              </span>
            </div>
            {feature.properties.description && (
              <div className="mt-2 text-xs text-gray-500 truncate">
                {feature.properties.description || (showAllTooltips ? '' : 'Tap to view details')}
              </div>
            )}
          </div>
        </Popup>
      );
    } else {
      return (
        <Popup
          key={key}
          longitude={longitude}
          latitude={latitude}
          closeButton={false}
          closeOnClick={false}
          anchor="bottom"
          offset={[0, -5]} // Offset to position popup above the point
          className={showAllTooltips ? 'asset-popup all-features-popup' : 'asset-popup'}
        >
          <div className="p-2 text-sm">
            <div className="flex items-center gap-2 mb-1">
              <AssetImage 
                asset={feature.properties.asset}
                imageUrl={feature.properties.imageUrl}
                assetName={feature.properties.name}
                size={24}
                fallbackIcon={<HomeSmile className="h-3 w-3" />}
              />
              <strong>{feature.properties.name}</strong>
            </div>
            <div className="mt-1">
              {isValidForecastValue(feature?.properties?.forecastValue)
                ? `${feature?.properties?.forecastValue?.toFixed(2)} ${getForecastUnit(forecastType)}`
                : "N/A"}
            </div>
          </div>
        </Popup>
      );
    }
  };

  // Get coordinates from geometry
  const getCoordinates = (feature: Feature): [number, number] | null => {
    if (feature.geometry.type === 'Point') {
      return [
        feature.geometry.coordinates[0],
        feature.geometry.coordinates[1]
      ];
    } else {
      // For all other geometry types (Polygon, LineString, MultiPoint, MultiPolygon, MultiLineString),
      // use centroid which works universally
      const centroid = turf.centroid(feature);
      return [
        centroid.geometry.coordinates[0],
        centroid.geometry.coordinates[1]
      ];
    }
  };

  return (
    <>
      {/* Hovered Feature Popup */}
      {hoveredFeature && !showAllTooltips && 
        renderAssetPopup(
          hoveredFeature.feature, 
          hoveredFeature.longitude, 
          hoveredFeature.latitude
        )
      }
      
      {/* Show All Tooltips */}
      {showAllTooltips && assetGeoJSON && assetGeoJSON.features && 
        assetGeoJSON.features.map((feature, index) => {
          const coordinates = getCoordinates(feature);
          if (!coordinates) return null;
          
          return renderAssetPopup(
            feature, 
            coordinates[0], 
            coordinates[1], 
            `tooltip-${index}-${feature.properties.id}`
          );
        })
      }
    </>
  );
};

export default MapPopups;